import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthenticationService } from "./authentication.service";
import { EnvironmentService } from "./environment.service";

@Injectable({
  providedIn: "root",
})
export class SessionService {
  constructor(
    private http: HttpClient,
    private _authenticationService: AuthenticationService,
    private env: EnvironmentService
  ) {}

  dashboardSessionRedirectSort: string | null = null;

  parseErrorBlob(err: HttpErrorResponse): Observable<any> {
    const reader: FileReader = new FileReader();

    const obs = Observable.create((observer: any) => {
      reader.onloadend = (e) => {
        observer.error(JSON.parse(reader.result.toString()));
        observer.complete();
      };
    });
    reader.readAsText(err.error);
    return obs;
  }

  getAllSessions() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Sessions/GetAllSessions";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllSessionsForUser(loggedInUser) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/GetSessionForUser/User/${loggedInUser}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getAllProcesses() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Sessions/GetAllProcesses";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  requestSessionFromDeployment(
    depName: string,
    requirementObj: any,
    nodeGroup: string,
    user: string,
    timePeriod: any,
    desktopRequest: boolean = false
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `User/${user}/Deployment/${depName}/Connect`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Platform", requirementObj.platform)
      .set("TimePeriod", `${timePeriod}`)
      .set("Application", `${requirementObj.applicationName}`)
      .set("NodeGroup", nodeGroup)
      .set("DesktopRequest", desktopRequest.toString())
      .set("ApplicationVersion", `${requirementObj.applicationVersion}`);

    return this.http.get(baseUrl, { headers: headers });
  }

  checkSessionState(sessionUID: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/SessionState/${sessionUID}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getClientSessionsForUser(userName) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/Client/User/${userName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  removeSession(
    nodeName: string,
    userName: string,
    depName: string,
    initiator: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/Remove/${nodeName}/${userName}/${depName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("Initiator", initiator);
    return this.http.delete(baseUrl, { headers: headers });
  }

  getSessionData(user, type, days) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `ActionLogs/User/${user}/GetSessionLogs/${type}/Days=${days}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  addTrackedApplications(processNames: Array<any>, isTracked: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      "Sessions/SetProcesses";
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("isTracked", isTracked);
    return this.http.post(baseUrl, processNames, { headers: headers });
  }

  // this function only fetches the file data

  connectSession(nodeName: string, userName: string, depName: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/Connect/${depName}/${nodeName}/${userName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  connectSessionClient(
    nodeName: string,
    userName: string,
    depName: string,
    userinfo: any,
    authType: string
  ) {
    let token = userinfo.token;
    let baseUrl =
      this.env.envVariables.VDIClientAPI +
      `/SyncVdiDesktop/Sessions/get/${depName}/${userName}/${nodeName}/${authType}/${token}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl);
  }

  triggerApplicationFromClient(response) {
    let baseUrl = this.env.envVariables.VDIClientAPI + `/trigger`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.post(baseUrl, response, { headers: headers });
  }

  extendSession(
    uniqueId: string,
    userName: string,
    wsName: string,
    timePeriod: string
  ) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/ExtendSession/${wsName}/${userName}/${timePeriod}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache")
      .set("UniqueId", uniqueId);
    return this.http.put(baseUrl, { headers: headers });
  }

  downloadSessionInfo() {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/DownloadSessionInfo`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Cache-Control", "no-cache");
    return this.http.get(baseUrl, { headers: headers });
  }

  getSessions(): Observable<any> {
    // Set the headers for the request
    const apiUrl = "http://localhost:5011/api/SyncVdiDesktop/Sessions/get";
    const headers = new HttpHeaders({
      NodeName: "machine2",
      Deployment: "allwindows",
      UserName: "hpcadmin",
    });

    // Make the HTTP GET request with the headers
    return this.http.get(apiUrl, { headers });
  }

  openApplicationSession(applicationName: string, applicationVersion: string) {
    let baseUrl =
      this.env.envVariables.httpProtocol +
      this.env.envVariables.webHost +
      `Sessions/OpenApplication/${applicationName}`;
    let headers = new HttpHeaders();
    headers = headers
      .set("Content-Type", "application/json; charset=utf-8")
      .set("Application", applicationName)
      .set("Version", applicationVersion);
    return this.http.get(baseUrl, { headers: headers });
  }
}
